import "@blueprintjs/core/lib/css/blueprint.css";
import "@blueprintjs/popover2/lib/css/blueprint-popover2.css";

import "@blueprintjs/datetime/lib/css/blueprint-datetime.css";
import "@blueprintjs/icons/lib/css/blueprint-icons.css";
import "@blueprintjs/table/lib/css/table.css";
import "react-phone-input-2/lib/style.css";
import "filepond/dist/filepond.min.css";
import "normalize.css/normalize.css";

import { Classes, Colors, Drawer, Position, Spinner } from "@blueprintjs/core";
import { ErrorBoundary, Footer } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";

import { AppNavigation } from "./app-navigation";
import { AppRouter } from "./AppRouter";
import { DialogRoot, Header, NavigationMenu } from "./components";
import { useGlobalData } from "./contexts";
import { useAuth } from "./contexts";
import { useAutoCheckVersionRefresh, useLocalStorage } from "./hooks";
import { LoginRouter } from "./LoginRouter";

interface IAppProps {}

const AppContainer = styled.div<{ isDark: boolean }>`
  display: flex;
  flex: 1;
  height: -webkit-fill-available;
  width: 100%;
  flex-direction: column;
  background-color: ${(props) => (props.isDark ? Colors.DARK_GRAY2 : Colors.LIGHT_GRAY3)};
`;

const ContentContainer = styled.div`
  padding-top: 75px;
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  width: 100%;
  flex-wrap: wrap;
`;

const CenterContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-top: 100px;
  flex: 1;
`;

export const App: React.FunctionComponent<IAppProps> = (props) => {
  useAutoCheckVersionRefresh();
  const [menuFixed, setMenuFixed] = useLocalStorage("menuFixed", true);
  const { user, userPermissions } = useAuth();
  const [drawerVisible, setDrawerVisible] = React.useState(false);
  const [darkMode, setDarkMode] = React.useState(false);

  const isChangePassword = React.useMemo(() => window.location.href.includes("changePassword"), [window.location.href]); //this dependency is mandatory

  const { loading, appVersion } = useGlobalData();
  const navigationItems = React.useMemo(
    () => AppNavigation(userPermissions?.map((p) => p.module.toUpperCase()) ?? []),
    [userPermissions]
  );
  return (
    <AppContainer isDark={darkMode} className={darkMode ? Classes.DARK : ""}>
      <ErrorBoundary>
        <Header
          showMenuIcon={!menuFixed}
          toggleLeftMenu={() => {
            setDrawerVisible((s) => !s);
          }}
          isDark={darkMode}
          onDarkSwitchChange={() => setDarkMode(!darkMode)}
        />
        {!user ? (
          <CenterContainer className="page">
            <LoginRouter />
          </CenterContainer>
        ) : (
          <>
            {loading || !userPermissions ? (
              <CenterContainer>
                <Spinner />
              </CenterContainer>
            ) : (
              <ContentContainer className="page">
                {isChangePassword ? null : (
                  <Drawer
                    hasBackdrop={true}
                    className={Classes.DARK}
                    isOpen={drawerVisible}
                    position={Position.LEFT}
                    size={350}
                    onClose={() => setDrawerVisible(false)}
                    canEscapeKeyClose={true}
                    canOutsideClickClose={true}
                    usePortal={false}
                  >
                    <div className={Classes.DRAWER_BODY}>
                      <NavigationMenu
                        collapseOtherOnSelection={true}
                        maxAutoCollapseLevel={0}
                        navigationMenu={navigationItems}
                        isFixed={menuFixed}
                        changeFixed={() => {
                          if (!menuFixed) {
                            setDrawerVisible(false);
                          }
                          setMenuFixed(!menuFixed);
                        }}
                        onNavigation={() => setDrawerVisible(false)}
                      />
                    </div>
                    <div className={Classes.DRAWER_FOOTER} />
                  </Drawer>
                )}
                {menuFixed && !isChangePassword && (
                  <NavigationMenu
                    navigationMenu={navigationItems}
                    collapseOtherOnSelection={true}
                    maxAutoCollapseLevel={0}
                    isFixed={menuFixed}
                    changeFixed={() => setMenuFixed(!menuFixed)}
                  />
                )}

                <AppRouter />
              </ContentContainer>
            )}
            <Footer versionLoading={loading} version={appVersion} />
          </>
        )}
        <DialogRoot />
      </ErrorBoundary>
    </AppContainer>
  );
};
